import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/magento-zu-shopify";
const TITLE = "Magento zu Shopify migrieren: Ablauf, Grenzen, Kosten";
const DESCRIPTION =
  "Magento 2 oder Magento 1 zu Shopify migrieren: Datenübernahme, Attribut- und Store-View-Logik, Weiterleitungen und realistische Kosten für den Umzug.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Bei einer Migration von Magento zu Shopify sind nicht die Produktdaten das Problem, sondern die Struktur: Attributsets, Store Views und Kundengruppen haben in Shopify keine direkte Entsprechung. Planen Sie Datenexport, Neuabbildung der Struktur in Collections und Metafeldern, einen vollständigen 301-Plan und einen gestaffelten Livegang, üblicherweise über sechs bis zwölf Wochen.";

const faqs = [
  {
    q: "Kann ich Magento-Daten nach Shopify übernehmen?",
    a: "Produkte, Kunden und Bestellungen lassen sich per CSV oder über die Magento-API exportieren und in Shopify importieren. Attributsets und konfigurierbare Produkte müssen dabei auf Shopify-Varianten, Tags und Metafelder abgebildet werden, hier entsteht der eigentliche Aufwand.",
  },
  {
    q: "Was passiert mit mehreren Store Views und Sprachen?",
    a: "Magento-Store-Views werden in Shopify über Märkte und Übersetzungen abgebildet. Bei sehr unterschiedlichen Sortimenten je Land kann auch mehr als ein Shop sinnvoll sein, das gehört vor der Migration entschieden.",
  },
  {
    q: "Ist Shopify für einen Magento-Shop leistungsfähig genug?",
    a: "Für die meisten Sortimente ja, inklusive großer Produktzahlen. Eng wird es bei sehr individueller Preis- oder Bestelllogik und tiefen Eingriffen in den Kaufabschluss, die erst in den höheren Tarifen möglich sind.",
  },
  {
    q: "Was kostet eine Magento-zu-Shopify-Migration?",
    a: "Realistisch sind 8.000 bis 25.000 € netto, abhängig von Attributstruktur, Sprachen, ERP-Anbindung und B2B-Logik. Entwicklung berechne ich mit 110 € netto pro Stunde, Conversion-Optimierung ab 3.990 € netto.",
  },
  {
    q: "Wie lange dauert der Umzug?",
    a: "Sechs bis zwölf Wochen sind üblich. Getrieben wird der Zeitplan von Datenbereinigung, Schnittstellen und dem Weiterleitungsplan, nicht von der reinen Produktzahl.",
  },
];

export const Route = createFileRoute("/ratgeber/magento-zu-shopify")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Migration",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Migration"
      title="Magento zu Shopify: Struktur schlägt Datenmenge"
      intro="Magento-Shops scheitern beim Umzug selten am Produktexport. Sie scheitern an Attributsets, Store Views und gewachsenen Modulen. Wer diese Punkte vorher klärt, bekommt einen Shop mit weniger Wartung und schnelleren Seiten."
      source="ratgeber-magento-zu-shopify"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Der typische Anlass",
          p: [
            "Magento 1 wird nicht mehr gepflegt, und Magento 2 bindet laufend Entwicklungsbudget für Updates, Hosting und Sicherheit. Wenn das Team mehr Zeit in Wartung als in Sortiment und Marketing steckt, ist der Wechsel eine Rechenaufgabe, keine Geschmacksfrage.",
          ],
        },
        {
          h: "Was den Aufwand wirklich treibt",
          p: [],
          list: [
            "Attributsets und konfigurierbare Produkte auf Shopify-Varianten abbilden",
            "Store Views und Sprachen über Märkte und Übersetzungen lösen",
            "Kundengruppen und Staffelpreise in B2B-Logik überführen",
            "Eigene Module ersetzen oder als App bzw. Entwicklung neu bauen",
            "Bestehende ERP- und PIM-Schnittstellen neu anbinden",
          ],
        },
        {
          h: "Reihenfolge, die funktioniert",
          p: [],
          list: [
            "Bestandsaufnahme aller Adressen mit Sichtbarkeit und Umsatz",
            "Datenexport und Bereinigung, doppelte Attribute zusammenführen",
            "Zielstruktur in Shopify festlegen, bevor importiert wird",
            "Theme aufbauen und Produktseite bewusst neu strukturieren",
            "301-Plan setzen, testen und erst dann live gehen",
          ],
        },
        {
          h: "Wo Shopify an Grenzen kommt",
          p: [
            "Sehr individuelle Preis- und Bestelllogik, komplexe Konfiguratoren und tiefe Checkout-Eingriffe sind machbar, kosten aber Entwicklungszeit oder eine App. Diese Fälle gehören vor die Entscheidung, nicht in die Umsetzungsphase.",
          ],
        },
        {
          h: "Kostenrahmen",
          p: [
            "Realistisch sind 8.000 bis 25.000 € netto. Entwicklung rechne ich mit 110 € netto pro Stunde ab, Conversion-Optimierung ab 3.990 € netto. Nach Sichtung des Shops gibt es ein festes Angebot statt einer Schätzung.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-migration", label: "Shopify Migration im Überblick" },
        { to: "/ratgeber/shopware-zu-shopify", label: "Shopware zu Shopify" },
        { to: "/shopify-plus", label: "Shopify Plus im Vergleich" },
        { to: "/kontakt", label: "Migration anfragen" },
      ]}
    />
  );
}
